import styled from 'styled-components'
import { TIMELINE_DASH } from '../timelineRail.styles'
import {
	darkOrange,
	orange,
	gold,
	black,
	textMuted,
	darkGrey,
	mutedGrey,
	lightGrey,
	white,
	surface2,
	border1,
	border2,
} from '@/theme/colors'
import { shadows, radii, space, motion, media, layers } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { flatButton, dashedSlot } from '@/theme/recipes'
import { cardSurface } from '@components/Card'

/** A day column: a card that lights up while an event is dragged over it. */
export const ColumnContainer = styled.div<{ $isDraggingOver?: boolean }>`
	${cardSurface}
	padding: ${space[4]};
	border: 2px solid transparent;
	min-width: 350px;
	max-width: 410px;
	max-height: 100%;
	display: flex;
	flex-direction: column;
	transition:
		border-color ${motion.base},
		box-shadow ${motion.base};

	${({ $isDraggingOver }) =>
		$isDraggingOver &&
		`
		border-color: ${orange};
		box-shadow: ${shadows.cardHover};
	`}

	${media.phone} {
		min-width: calc(100vw - 32px);
		max-width: calc(100vw - 32px);
		padding: 14px;
	}
`

export const ColumnHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 12px;
	padding-bottom: 12px;
	border-bottom: 1px solid ${border1};
	flex-shrink: 0;
`

export const ColumnTitle = styled.h2`
	${text.title}
	color: ${black};
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`

export const DateDisplay = styled.span`
	${text.bodyXs}
	color: ${textMuted};
`

export const CustomDayName = styled.span`
	${text.label}
	color: ${gold};
`

/* Day-note affordances keep the gold secondary role. */
export const EditDayInfoButton = styled.button`
	${flatButton}
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	margin-top: 2px;
	padding: 4px 8px;
	border: 1px solid ${border2};
	border-radius: ${radii.small};
	color: ${mutedGrey};
	font-size: ${fontSizes.micro};
	white-space: nowrap;
	transition:
		border-color ${motion.fast},
		color ${motion.fast},
		background-color ${motion.fast};

	&:hover {
		border-color: ${gold};
		color: ${gold};
		background: ${gold}14;
	}
`

export const DayNoteRow = styled.button`
	${flatButton}
	width: 100%;
	text-align: left;
	background: ${gold}1a;
	border: 1px solid ${gold}66;
	border-radius: ${radii.small};
	padding: 10px 12px;
	margin-bottom: 12px;
	display: flex;
	align-items: flex-start;
	gap: 8px;
	flex-shrink: 0;
	transition:
		background-color ${motion.fast},
		border-color ${motion.fast};

	&:hover {
		background: ${gold}33;
		border-color: ${gold};
	}
`

export const StaticDayNoteRow = styled(DayNoteRow)`
	cursor: default;
`

export const DayNoteIcon = styled.span`
	font-size: ${fontSizes.small};
	flex-shrink: 0;
	margin-top: 1px;
`

export const DayNoteText = styled.span`
	font-size: ${fontSizes.caption};
	color: ${darkOrange};
	line-height: ${leading.body};
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
`

export const AddNoteRow = styled.button`
	${flatButton}
	width: 100%;
	text-align: left;
	border: 1px dashed ${border2};
	border-radius: ${radii.small};
	padding: 8px 12px;
	margin-bottom: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
	color: ${mutedGrey};
	font-size: ${fontSizes.caption};
	flex-shrink: 0;
	transition:
		border-color ${motion.fast},
		color ${motion.fast},
		background-color ${motion.fast};

	&:hover {
		border-color: ${gold};
		color: ${gold};
		background: ${gold}14;
	}
`

export const ColumnContent = styled.div`
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	min-height: 0;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: ${surface2};
		border-radius: ${radii.round};
	}

	&::-webkit-scrollbar-thumb {
		background: ${lightGrey};
		border-radius: ${radii.round};

		&:hover {
			background: ${darkGrey};
		}
	}

	scrollbar-width: thin;
	scrollbar-color: ${lightGrey} ${surface2};
`

export const ButtonsContainer = styled.div`
	display: flex;
	gap: 8px;
	flex-shrink: 0;
	padding-top: 8px;
`

export const AddEventButton = styled.button`
	${dashedSlot}
	flex: 1;
`

export const EmptyState = styled.div`
	text-align: center;
	color: ${textMuted};
	padding: 32px 16px;
	border: 2px dashed ${border2};
	border-radius: ${radii.small};
	background: ${surface2};
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`

export const EmptyStateText = styled.div`
	font-size: ${fontSizes.small};
	color: ${textMuted};
	margin: 0;
`

export const DraggedEventPlaceholder = styled.div`
	height: 0;
	overflow: hidden;
`

export const TimelineEventRow = styled.div`
	display: flex;
	align-items: stretch;
`

/** Dot centre below an event row's top: the 31px lead-in + half the 10px dot. */
export const EVENT_DOT_ANCHOR = 36

/** Hosts the absolutely-positioned `RailConnector` down to the next row's anchor. */
export const EventDotColumn = styled.div`
	width: 24px;
	flex-shrink: 0;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 31px;
`

export const EventDot = styled.div`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: ${white};
	border: 2px solid ${TIMELINE_DASH};
	flex-shrink: 0;
	position: relative;
	z-index: ${layers.raised};
`

export const EventContent = styled.div`
	flex: 1;
	min-width: 0;
	padding-left: 10px;
	padding-top: 10px;
	padding-bottom: 10px;
`
